import { getMonthTwoDigits } from '../../utils/date';
import { RequestStatus } from '../../shared/Requests';
import React, { createContext, Reducer, useReducer, useState, useMemo } from 'react';
import BillingDailyEngagedUsersServiceApi from '../../api/Billing/BillingDailyEngagedUsersApi';
import { CompiledDailyEngagedUserConsumption } from '../../models/billing/dailyEngagedUsers/CompiledDailyEngagedUserConsumption';
import {
  BillingMetrics,
  getMetricStatus,
  MetricStatus,
  METRIC_NOT_REGISTERED,
  UNLIMITED_PLAN_METRIC,
} from '../../shared/BillingMetrics';
import FeatureToggleService from '../../services/FeatureToggleService';

interface CompiledDailyEngagedUserDataState {
  compiledDailyEngagedUserConsumption: CompiledDailyEngagedUserConsumption[];
  compiledContainsDataOnSelectedMonth: boolean;
}

interface DeuConsumptionContextData {
  compiledDailyEngagedUserDataState: CompiledDailyEngagedUserDataState;
  consolidatedDeuApiRequisition: (
    dateToSearch: Date,
    hasEnabledMetric: (value: Array<BillingMetrics>) => boolean,
  ) => Promise<void>;
  isDeuLoading: boolean;
  deuStatus: MetricStatus;
}

interface Props {
  tenant: any;
}

export const DeuConsumptionContext = createContext<DeuConsumptionContextData>({} as DeuConsumptionContextData);

export const DeuConsumptionContextProvider: React.FC<Props> = ({ children, tenant }) => {
  const [isDeuLoading, setIsDeuLoading] = useState(false);
  const [deuStatus, setDeuStatus] = useState(MetricStatus.error);
  const billingDailyEngagedUserApi = new BillingDailyEngagedUsersServiceApi();
  const compiledDailyEngagedUserConsumption = new CompiledDailyEngagedUserConsumption(
    new CompiledDailyEngagedUserConsumption(null),
  );

  const [compiledDailyEngagedUserDataState, setCompiledDailyEngagedUserDataState] = useReducer<
    Reducer<CompiledDailyEngagedUserDataState, Partial<CompiledDailyEngagedUserDataState>>
  >(
    (compiledDailyEngagedUserDataState, newCompiledDailyEngagedUserDataState) => ({
      ...compiledDailyEngagedUserDataState,
      ...newCompiledDailyEngagedUserDataState,
    }),
    {
      compiledDailyEngagedUserConsumption: [compiledDailyEngagedUserConsumption],
      compiledContainsDataOnSelectedMonth: false,
    },
  );

  const consolidatedDeuApiRequisition = async (
    dateToSearch: Date,
    hasEnabledMetric: (value: Array<BillingMetrics>) => boolean,
  ): Promise<void> => {
    try {
      setIsDeuLoading(true);

      const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
      const storageType = await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.dailyEngagedUser) ? 1 : 0;

      const apiData = hasEnabledMetric([BillingMetrics.dailyEngagedUser])
        ? await billingDailyEngagedUserApi.getCompiledDailyEngagedUserConsumption(tenant.info.id, monthString, storageType)
        : [compiledDailyEngagedUserConsumption];

      const compiledDeuConsumption = apiData.map(item => new CompiledDailyEngagedUserConsumption(item));

      setCompiledDailyEngagedUserDataState({
        compiledDailyEngagedUserConsumption: compiledDeuConsumption,
        compiledContainsDataOnSelectedMonth: billingDailyEngagedUserApi.getStatus() === RequestStatus.SUCCESS,
      });

      const dailyMetric = compiledDeuConsumption.find(
        item => item.metric.metricId === BillingMetrics.dailyEngagedUser
      );

      if (billingDailyEngagedUserApi.getStatus() === RequestStatus.SUCCESS && dailyMetric) {
        setDeuStatus(
          getMetricStatus(
            dailyMetric.metric.quantity === UNLIMITED_PLAN_METRIC
              ? UNLIMITED_PLAN_METRIC
              : dailyMetric.planUsageRate,
          ),
        );
      } else {
        setDeuStatus(getMetricStatus(METRIC_NOT_REGISTERED));
      }

    } finally {
      setIsDeuLoading(false);
    }
  };

  const contextValue = useMemo(() => ({
    compiledDailyEngagedUserDataState,
    consolidatedDeuApiRequisition,
    isDeuLoading,
    deuStatus,
  }), [compiledDailyEngagedUserDataState, consolidatedDeuApiRequisition, isDeuLoading, deuStatus]);

  return (
    <DeuConsumptionContext.Provider value={contextValue}>
      {children}
    </DeuConsumptionContext.Provider>
  );
};

export function useDeuConsumptionContext(): DeuConsumptionContextData {
  const context = React.useContext(DeuConsumptionContext);

  if (!context) {
    throw new Error('use deuconsumptioncontext context must be used within an DeuConsumptionContextProvider');
  }

  return context;
}
